/**
 * Session-only editor tracing overlay. The bitmap never enters the map JSON.
 * Survives test-play because EditorScene.create() resets instance fields.
 */

import { length, sub } from '../../sim/math.ts';
import { snapPoint, snapValue } from '../../sim/map/snap.ts';
import type { Rect } from '../../sim/map/selection.ts';
import type { MapBounds, Vec2 } from '../../sim/map/types.ts';

export const REFERENCE_IMAGE_TEXTURE_KEY = 'editor-reference-image';
export const REFERENCE_FIT_FRACTION = 0.9;
export const REFERENCE_MIN_SIZE = 64;

export type ReferenceImageRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ReferenceImageSession = ReferenceImageRect & {
  objectUrl: string;
  fileName: string;
  sourceWidth: number;
  sourceHeight: number;
  visible: boolean;
};

let session: ReferenceImageSession | null = null;

export function getReferenceImageSession(): ReferenceImageSession | null {
  return session;
}

export function hasReferenceImage(): boolean {
  return session !== null;
}

function revokeObjectUrl(url: string): void {
  try {
    URL.revokeObjectURL(url);
  } catch {
    // Already revoked, or a test stub that is not a real blob URL.
  }
}

export function setReferenceImageSession(next: ReferenceImageSession): void {
  if (session && session.objectUrl !== next.objectUrl) {
    revokeObjectUrl(session.objectUrl);
  }

  session = next;
}

export function patchReferenceImageSession(
  patch: Partial<ReferenceImageSession>,
): ReferenceImageSession | null {
  if (!session) {
    return null;
  }

  session = { ...session, ...patch };
  return session;
}

export function clearReferenceImageSession(): void {
  if (session) {
    revokeObjectUrl(session.objectUrl);
  }

  session = null;
}

/** Contain the bitmap in the map at `REFERENCE_FIT_FRACTION`, centred. */
export function fitReferenceInBounds(
  sourceW: number,
  sourceH: number,
  bounds: MapBounds,
): ReferenceImageRect {
  const cx = bounds.width / 2;
  const cy = bounds.height / 2;

  if (!(sourceW > 0) || !(sourceH > 0) || !(bounds.width > 0) || !(bounds.height > 0)) {
    return { x: cx, y: cy, width: 1, height: 1 };
  }

  const scale = Math.min(
    (bounds.width * REFERENCE_FIT_FRACTION) / sourceW,
    (bounds.height * REFERENCE_FIT_FRACTION) / sourceH,
  );

  return {
    x: cx,
    y: cy,
    width: sourceW * scale,
    height: sourceH * scale,
  };
}

export function referenceImageFrame(rect: ReferenceImageRect): Rect {
  const halfW = rect.width / 2;
  const halfH = rect.height / 2;
  return {
    minX: rect.x - halfW,
    minY: rect.y - halfH,
    maxX: rect.x + halfW,
    maxY: rect.y + halfH,
  };
}

/**
 * Uniform scale about a fixed opposite-corner `origin`. `minSize` is the floor
 * for the shorter side so a collapsed drag cannot hide the handles.
 */
export function scaleReferenceRect(
  rect: ReferenceImageRect,
  origin: Vec2,
  pointer: Vec2,
  options: { minSize?: number; snapStep?: number } = {},
): ReferenceImageRect {
  const minSize = options.minSize ?? REFERENCE_MIN_SIZE;
  const start = sub({ x: rect.x, y: rect.y }, origin);
  // Pointer is the dragged corner; the centre is halfway to the fixed origin.
  const startLen = length(start) * 2;

  if (startLen < 1e-6) {
    return { ...rect };
  }

  let factor = length(sub(pointer, origin)) / startLen;

  if (options.snapStep !== undefined) {
    factor = snapValue(factor, options.snapStep);
  }

  const shorter = Math.min(rect.width, rect.height);
  const minFactor = shorter > 0 ? minSize / shorter : 1;
  factor = Math.max(factor, minFactor);

  return {
    x: origin.x + start.x * factor,
    y: origin.y + start.y * factor,
    width: rect.width * factor,
    height: rect.height * factor,
  };
}

export function moveReferenceRect(
  rect: ReferenceImageRect,
  delta: Vec2,
  snapStep?: number,
): ReferenceImageRect {
  let x = rect.x + delta.x;
  let y = rect.y + delta.y;

  if (snapStep !== undefined && snapStep > 0) {
    const snapped = snapPoint({ x, y }, snapStep);
    x = snapped.x;
    y = snapped.y;
  }

  return { ...rect, x, y };
}

export function loadHtmlImage(objectUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('decode'));
    image.src = objectUrl;
  });
}
